import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva } from "class-variance-authority";

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
        outline:
          "border border-input shadow-sm hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      /* Socle 2026-08 : cible tactile 44 px sur mobile (gants), densité
         d'origine conservée à partir de md — supprime le besoin des
         rustines min-h-[44px] éparpillées dans le code. */
      size: {
        default: "h-11 md:h-9 px-4 py-2",
        /* `sm` était resté à h-9 (36 px) sur mobile quand le socle de 44 px a
           été posé en août : `default`, `lg` et `icon` l'avaient, pas lui. Or
           c'est la taille des barres d'outils — « Exporter », « Export Silae »,
           « Variables de paie », « Nouvelle tâche », les flèches de mois. Sur
           mobile la densité cède au doigt ; à partir de md, rien ne change. */
        sm: "h-11 md:h-8 rounded-md px-3 text-xs",
        lg: "h-12 md:h-10 rounded-md px-8",
        /* `shrink-0` : un bouton d'icône a une taille, pas une préférence. Sans
           lui, `flex-shrink` valant 1 par défaut, une barre trop pleine les
           écrasait à 16 px de large — la hauteur restait à 44, la largeur non,
           et la mesure BUG-039 les comptait « trop petits » alors qu'ils
           étaient bien déclarés à 44. Une barre qui déborde doit céder sur son
           CONTENU, pas sur la taille de ses cibles. */
        icon: "h-11 w-11 md:h-9 md:w-9 shrink-0",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

const Button = React.forwardRef(({ className, variant, size, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : "button"
  return (
    <Comp
      className={cn(buttonVariants({ variant, size, className }))}
      ref={ref}
      {...props} />
  );
})
Button.displayName = "Button"

export { Button, buttonVariants }
